import React, { useState } from "react";
import { Button } from "primereact/button";
import { format, addMonths, subMonths } from "date-fns";
import PackagingCalendarViewModel from "../../viewmodels/packaging/PackagingCalendarViewModel";
import CalendarGrid from "../../components/calendar/CalendarGrid";
import EmptyModal from "../../components/modals/EmptyModal";
import PackagingDayHistoryModal from "../../components/calendar/PackagingDayHistoryModal";

export default function PackagingCalendar({ addSnackbar, logout }) {
  const vm = PackagingCalendarViewModel(addSnackbar, logout);
  const [expandedDay, setExpandedDay] = useState(null);

  return (
    <div className="p-4">
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Packaging Calendar</h1>
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-lg shadow-sm p-1 flex items-center gap-3">
            <button
              onClick={() => vm.onChangeMonth(subMonths(vm.currentMonth, 1))}
              aria-label="Previous month"
              title="Previous month"
              disabled={vm.loadingCalendar}
              className={`inline-flex items-center justify-center w-10 h-10 rounded-md ${
                vm.loadingCalendar ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-700"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  d="M12 6L8 10l4 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div className="text-base font-semibold px-4 text-gray-800">
              {format(vm.currentMonth, "MMMM yyyy")}
            </div>

            <button
              onClick={() => vm.onChangeMonth(addMonths(vm.currentMonth, 1))}
              aria-label="Next month"
              title="Next month"
              disabled={vm.loadingCalendar}
              className={`inline-flex items-center justify-center w-10 h-10 rounded-md ${
                vm.loadingCalendar ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-700"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  d="M8 6l4 4-4 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <Button
            label="Today"
            disabled={vm.loadingCalendar}
            className={`p-button-sm bg-main-purple text-white px-4 rounded-md shadow-sm ${
              vm.loadingCalendar ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={() => vm.onChangeMonth(new Date())}
          />
        </div>
      </header>
      <div className="relative">
        {vm.loadingCalendar && (
          <div className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center">
            <div className="flex items-center gap-3">
              <svg
                className="h-6 w-6 animate-spin text-main-purple"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              <div className="font-semibold text-gray-700">
                Loading calendar...
              </div>
            </div>
          </div>
        )}

        <CalendarGrid
          calendar={vm.calendar}
          onDayClick={(day) => {
            vm.getAnalytics(day.date);
            vm.handleDaySelect(day.date);
            vm.setSelectedDay(day);
            vm.openDayHistory(day.date);
          }}
          expandedDay={expandedDay}
          onMoreClick={(e, day) => {
            e.stopPropagation();
            setExpandedDay(day.date);
            vm.setSelectedDay(day);
          }}
          onShowLessClick={(e) => {
            e.stopPropagation();
            setExpandedDay(null);
          }}
          openEventDetails={(ev, Comp) => vm.openEventDetails(ev, Comp)}
        />
      </div>

      <EmptyModal open={vm.emptyModal.open} setOpen={vm.setEmptyModal}>
        {vm.emptyModal.childComponent}
      </EmptyModal>

      <PackagingDayHistoryModal
        open={vm.dayHistoryModal.open}
        analytics={vm.analytics}
        setOpen={(v) =>
          vm.setDayHistoryModal({ ...vm.dayHistoryModal, open: v })
        }
        date={vm.dayHistoryModal.date}
        formattedDate={vm.dayHistoryModal.formattedDate}
        items={vm.dayHistoryModal.items}
        loading={vm.dayHistoryModal.loading}
        onRefresh={() => {
          vm.getDayHistory(vm.dayHistoryModal.date);
          vm.getAnalytics(vm.dayHistoryModal.date);
        }}
        onView={(item) => vm.openEventDetails(item, () => null)}
      />
    </div>
  );
}
